import type { Category, Division, Format } from "@roxpacer/core";
import { useState } from "react";
import { useActivePlan, usePlans } from "../store";
import { DurationInput } from "./DurationInput";

const FORMATS: { value: Format; label: string }[] = [
  { value: "singles", label: "Singles" },
  { value: "doubles", label: "Doubles" },
  { value: "relay", label: "Relay" },
];

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string; disabled?: boolean }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex rounded-md bg-surface-2 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          disabled={option.disabled}
          onClick={() => onChange(option.value)}
          className={`h-8 flex-1 rounded px-3 text-sm font-medium disabled:opacity-40 ${
            value === option.value ? "bg-surface text-ink shadow-sm" : "text-ink-2 hover:text-ink"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function PlanSettings() {
  const plan = useActivePlan();
  const updateActive = usePlans((s) => s.updateActive);
  const setFormat = usePlans((s) => s.setFormat);
  const [nameDraft, setNameDraft] = useState<string | null>(null);

  return (
    <section className="card space-y-4 p-4" aria-labelledby="settings-heading">
      <h2 id="settings-heading" className="sr-only">
        Plan settings
      </h2>
      <label className="block space-y-1">
        <span className="label">Plan name</span>
        <input
          className="field w-full"
          value={nameDraft ?? plan.name}
          maxLength={100}
          onFocus={() => setNameDraft(plan.name)}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={() => {
            const name = nameDraft?.trim();
            if (name && name !== plan.name) updateActive((p) => ({ ...p, name }));
            setNameDraft(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <span className="label">Format</span>
          <Segmented label="Format" options={FORMATS} value={plan.format} onChange={setFormat} />
        </div>
        <div className="space-y-1">
          <span className="label">Category</span>
          <Segmented<Category>
            label="Category"
            options={[
              { value: "open", label: "Open" },
              { value: "pro", label: "Pro" },
            ]}
            value={plan.category}
            onChange={(category) => updateActive((p) => ({ ...p, category }))}
          />
        </div>
        <div className="space-y-1">
          <span className="label">Division</span>
          <Segmented<Division>
            label="Division"
            options={[
              { value: "men", label: "Men" },
              { value: "women", label: "Women" },
              { value: "mixed", label: "Mixed", disabled: plan.format === "singles" },
            ]}
            value={plan.division}
            onChange={(division) => updateActive((p) => ({ ...p, division }))}
          />
        </div>
      </div>

      <div className="space-y-1">
        <span className="label">{plan.athletes.length > 1 ? "Athletes" : "Athlete"}</span>
        <div className="grid gap-2 sm:grid-cols-2">
          {plan.athletes.map((athlete, i) => (
            <input
              key={athlete.id}
              aria-label={`Athlete ${i + 1} name`}
              className="field w-full"
              value={athlete.name}
              maxLength={60}
              onChange={(e) =>
                updateActive((p) => ({
                  ...p,
                  athletes: p.athletes.map((a) =>
                    a.id === athlete.id ? { ...a, name: e.target.value } : a,
                  ),
                }))
              }
            />
          ))}
        </div>
        {plan.format === "relay" && (
          <p className="text-xs text-muted">
            Each athlete covers two consecutive legs, in the order listed.
          </p>
        )}
      </div>

      {plan.format === "relay" && (
        <div className="flex items-center justify-between gap-3">
          <span className="label">Time per handover (×3)</span>
          <DurationInput
            label="Relay handover time"
            allowZero
            max={600}
            value={plan.relayHandoverSec ?? 0}
            onCommit={(relayHandoverSec) => updateActive((p) => ({ ...p, relayHandoverSec }))}
          />
        </div>
      )}
    </section>
  );
}
